import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import IndexPage from 'flarum/forum/components/IndexPage';
import LogInModal from 'flarum/forum/components/LogInModal';

/**
 * La vuelta del login para `/descargas/{slug}` (PKG-FORO-N8N-DOWNLOAD-TRACKER).
 *
 * El servidor manda al anónimo a `/?acceder=1&return=%2Fdescargas%2F<slug>`. Aquí se cierra el
 * círculo: en `/` con `acceder=1` y sin sesión se abre el modal de Acceder, y al entrar se
 * navega al `return` en vez del `location.reload()` del core — que dejaría a la persona en la
 * portada, con su descarga a medio camino.
 *
 * EL `return` SOLO SE HONRA SI ES UN PATH RELATIVO DEL PROPIO FORO: tiene que empezar por `/`,
 * no por `//` (protocol-relative te saca del origen), sin `\` (los navegadores normalizan
 * `/\` a `//`) y sin caracteres de control ni espacios — el parser de URL DESCARTA tab/CR/LF
 * antes de resolver, así que `/\n/evil.com` se volvería `//evil.com`. Cualquier otra cosa se
 * ignora y gana el reload del core. El servidor construye ese valor él mismo (nunca de una URL
 * del request), así que esta guarda es el segundo cinturón, no el primero.
 */
function safeReturnPath(): string | null {
  const raw = new URLSearchParams(window.location.search).get('return');

  if (!raw) return null;
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.includes('\\')) return null;
  if (/[\u0000-\u0020\u007f]/.test(raw)) return null;

  return raw;
}

/**
 * PKG-FORO-MODAL-RICK — por qué el modal no se abría (bug de #676, ahora corregido).
 *
 * `oncreate`/`onupdate` los llama Mithril DESDE DENTRO de su propio ciclo de redraw. Todos
 * los demás `app.modal.show(...)` del paquete cuelgan de un `onclick` — fuera de cualquier
 * redraw, así que nunca chocan. Este es el único que colgaba de un lifecycle hook: `modal.show`
 * fuerza un `m.redraw.sync()` interno, y llamarlo mientras Mithril ya está en medio de OTRO
 * redraw.sync es exactamente lo que Mithril rechaza con «Nested m.redraw.sync() call» — y como
 * la excepción no está capturada, el propio `show()` nunca termina de correr: el modal se queda
 * sin instanciar. Solución: sacar la llamada de la pila síncrona actual con un `setTimeout(0)`.
 * Un macrotask nuevo corre después de que el redraw en curso termina — lo mismo que pasa con un
 * clic real, que es por lo que esos otros `onclick` nunca tuvieron el problema.
 *
 * `this.umbralAccederHandled` vive en la instancia del componente (Mithril liga `this` a ella
 * en los lifecycle hooks de un componente de clase), así que un mismo montaje de `IndexPage`
 * solo intenta abrir el modal una vez, sin importar cuántas veces se repita `oncreate`/
 * `onupdate` — necesario porque ahora se cuelga también de `onupdate`, para el caso (raro pero
 * real) de que `acceder=1` aparezca en la URL por un cambio de query dentro de la SPA sin que
 * `IndexPage` se vuelva a montar.
 */
function accederRequested(): boolean {
  return new URLSearchParams(window.location.search).get('acceder') === '1';
}

function maybeOpenAccederModal(this: any): void {
  // El flag primero: `onupdate` puede dispararse muchas veces por el tiempo de vida de
  // `IndexPage` (cualquier redraw global la toca mientras la ruta sea `/`), y una vez atendido
  // el viaje no hace falta volver a parsear `location.search` en cada una.
  if (this.umbralAccederHandled) return;
  if (!accederRequested()) return;

  const target = safeReturnPath();

  if (app.session.user) {
    // Con sesión ya no hay nada que pedir: se completa el viaje directamente (el caso real
    // es entrar desde otra pestaña entre el 302 y este render).
    this.umbralAccederHandled = true;
    if (target) window.location.assign(target);
    return;
  }

  this.umbralAccederHandled = true;

  setTimeout(() => {
    // Re-chequeo de sesión Y de `acceder=1`: si algo más navegó en la ventana entre el
    // `oncreate`/`onupdate` y este macrotask, no queremos un modal huérfano de un viaje que
    // ya no está en la URL.
    if (!app.session.user && accederRequested()) app.modal.show(LogInModal);
  }, 0);
}

export default function addLoginReturn() {
  extend(IndexPage.prototype, 'oncreate', maybeOpenAccederModal);
  extend(IndexPage.prototype, 'onupdate', maybeOpenAccederModal);

  override(LogInModal.prototype, 'onlogin', function (this: any, original: () => void) {
    const target = safeReturnPath();

    if (target) {
      window.location.assign(target);
      return;
    }

    original();
  });
}
